import React from 'react';
import classNames from 'classnames';
import { bool, node, oneOfType, string } from 'prop-types';

import { AvatarMedium } from '../../Avatar/Avatar';
import NamedLink from '../../NamedLink/NamedLink';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { ensureUser } from '../../../util/data';
import { isSuperhostActive } from '../../../util/superhost';
import { propTypes } from '../../../util/types';

import css from './HostInfoCard.module.css';

const HostInfoCard = props => {
  const { author, authorLink, intl, className, horizontal, bookingPanel, linkToProfile } = props;
  const ensuredAuthor = ensureUser(author);
  const isSuperhost = isSuperhostActive(ensuredAuthor);

  const profile = ensuredAuthor?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const displayName = profile.abbreviatedName || profile.displayName || '';
  const createdAt = ensuredAuthor?.attributes?.createdAt;
  const hostSinceYear =
    createdAt instanceof Date && !Number.isNaN(createdAt.getTime())
      ? createdAt.getFullYear()
      : null;

  const avgRating =
    typeof publicData.averageRating === 'number'
      ? publicData.averageRating
      : Number(publicData.averageRating);
  const totalReviews =
    typeof publicData.totalReviews === 'number'
      ? publicData.totalReviews
      : Number(publicData.totalReviews);
  const hasReviews = !Number.isNaN(avgRating) && totalReviews > 0;

  const nameNode = authorLink || <span className={css.hostName}>{displayName}</span>;

  const profileId = ensuredAuthor?.id?.uuid;
  const isClickable = linkToProfile && !!profileId;

  const content = (
    <>
      <div className={css.avatarColumn}>
        <AvatarMedium
          className={isSuperhost ? null : css.hostAvatar}
          user={ensuredAuthor}
          disableProfileLink
        />
      </div>
      <div className={css.info}>
        {isSuperhost ? (
          <div className={css.superhostLabel}>
            <FormattedMessage id="OrderPanel.hostSuperhostLabel" />
          </div>
        ) : null}
        <div className={css.nameRow}>{nameNode}</div>
        {hostSinceYear ? (
          <div className={css.hostSince}>
            <FormattedMessage id="OrderPanel.hostSince" values={{ year: hostSinceYear }} />
          </div>
        ) : null}
        {hasReviews ? (
          <div className={css.reviews}>
            <span className={css.star} aria-hidden="true">
              ★
            </span>
            <FormattedMessage
              id="OrderPanel.hostReviews"
              values={{
                rating: intl.formatNumber(avgRating, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }),
                count: totalReviews,
              }}
            />
          </div>
        ) : null}
      </div>
    </>
  );

  const rootClasses = classNames(
    css.root,
    {
      [css.horizontal]: horizontal,
      [css.bookingPanel]: bookingPanel,
      [css.clickable]: isClickable,
    },
    className
  );

  return isClickable ? (
    <NamedLink className={rootClasses} name="ProfilePage" params={{ id: profileId }}>
      {content}
    </NamedLink>
  ) : (
    <div className={rootClasses}>{content}</div>
  );
};

HostInfoCard.defaultProps = {
  className: null,
  authorLink: null,
  horizontal: false,
  bookingPanel: false,
  linkToProfile: false,
};

HostInfoCard.propTypes = {
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  className: string,
  horizontal: bool,
  bookingPanel: bool,
  linkToProfile: bool,
  intl: intlShape.isRequired,
};

export default injectIntl(HostInfoCard);
